import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { MSOData } from "../types";
import ErrorIcon from "@mui/icons-material/Error";
import { useOffenseData } from "../hooks";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box
          sx={{ pt: 3, display: "flex", flexDirection: "column", gap: "24px" }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export function InfoTabs({ offenseData, isCurrentFelonyDangerous }: { offenseData: MSOData, isCurrentFelonyDangerous: boolean }) {
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  // maximums
  let maximumPrisonTimeLink = null;
  const conditionalReleaseStatuteLink =
    "https://revisor.mo.gov/main/OneSection.aspx?section=558.011#:~:text=(2)%20%22Conditional%20release%22,division%20of%20probation%20and%20parole";

  if (
    offenseData["Maximum Prison Time Determined By Text"] ===
    "conditional release statute"
  ) {
    maximumPrisonTimeLink = conditionalReleaseStatuteLink;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
          TabIndicatorProps={{
            style: { backgroundColor: "#B30000", height: "4px" },
          }}
        >
          {/* TITLEs */}
          <Tab
            label="Min"
            {...a11yProps(0)}
            sx={{
              p: 0,
              fontSize: "16px",
              fontWeight: "700",
              minWidth: "unset",
              "&.Mui-selected": { color: "#B30000" },
            }}
          />
          <Tab
            label="Max"
            {...a11yProps(1)}
            sx={{
              m: "0 0 0 32px",
              p: 0,
              fontSize: "16px",
              fontWeight: "700",
              minWidth: "unset",
              "&.Mui-selected": { color: "#B30000" },
            }}
          />
        </Tabs>
      </Box>
      {/* CONTENT */}
      <TabPanel value={value} index={0}>
        <Typography fontWeight="600">
          Minimum prison time is set by{" "}
          <a
            target="_blank"
            rel="noreferrer"
            href={offenseData["Minimum Prison Time Determined By"].link}
          >
            {offenseData["Minimum Prison Time Determined By"].label}
          </a>
          .
        </Typography>
        {offenseData["Minimum Prison Time Determined By"].text ? (
          <Typography fontWeight="600">
            {offenseData["Minimum Prison Time Determined By"].text}
          </Typography>
        ) : (
          <Typography fontWeight="600">
            {offenseData["Minimum Parole Eligibility Text"]}
          </Typography>
        )}
        <Typography>
          Minimum parole eligibility is the earliest point at which someone is
          eligible for parole release consideration. Minimum parole eligibility
          in no way requires a parole release. In making release decisions, the
          parole board considers many factors including offense behavior, victim
          impact, criminal history, social history, institutional adjustment,
          including program participation, release plans and community attitude.
        </Typography>
        {!isCurrentFelonyDangerous && (
          <>
            <Typography
              fontWeight="600"
              display="inline-block"
              alignItems="center"
            >
              {offenseData["Minimum Prison Time Warning Text"] && (
                <>
                  <ErrorIcon
                    fontSize="inherit"
                    sx={{
                      color: "#B30000",
                      mb: "-2px",
                      mr: "2px",
                    }}
                  />
                  This offense may trigger a minimum prison term.
                </>
              )}
            </Typography>
            <Typography sx={{ mt: "-24px", mb: "24px" }}>
              {offenseData["Minimum Prison Time Warning Text"]}
            </Typography>
          </>
        )}
      </TabPanel>
      <TabPanel value={value} index={1}>
        <Typography fontWeight="600">
          Maximum prison time is set by the{" "}
          {maximumPrisonTimeLink !== null ? (
            <a target="_blank" rel="noreferrer" href={maximumPrisonTimeLink}>
              {offenseData["Maximum Prison Time Determined By Text"]}
            </a>
          ) : (
            <span>{offenseData["Maximum Prison Time Determined By Text"]}</span>
          )}
          .
        </Typography>
        <Typography fontWeight="600">
          {offenseData["Maximum Prison Time Served Text"]}
        </Typography>
        <Typography>
          Conditional release means the conditional discharge of an offender by
          the parole board, subject to conditions of release that the parole
          board deems reasonable to assist the offender to lead a law-abiding
          life, and subject to the supervision under the division of probation
          and parole.
        </Typography>
      </TabPanel>
    </Box>
  );
}
